import { useState } from 'react';
import { useDataHealth } from '../api/hooks';
import type { DataSource } from '../api/types';
import { levelOf, rank, thresholds, type Level } from './health';
import { K } from '../kabinet/atoms';
import { IconClose } from '../ui/icons';

/* ФОРМАТЫ — ОДИН РАЗ НА МОДУЛЬ, а не на каждый вызов: сборка формата
   стоит в полсотни раз дороже самого форматирования, а полоса свежести
   рисует время четырежды и обновляется ежеминутно. См. `ui/chisla.ts`. */
const ЧАСЫ = new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Moscow',
});
const ДАТА = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Europe/Moscow',
});

/**
 * Плашка достоверности данных (13.0).
 *
 * ГЛАВНЫЙ ПРИНЦИП ЭТОГО ИНТЕРФЕЙСА: ни одно число не показывается без
 * указания, насколько ему можно верить. Это то, чем нельзя пожертвовать
 * ради красоты экрана, и то, что отличает продукт от конкурентов.
 *
 * ПЛАШКА ОДНА НА ВЕСЬ КАБИНЕТ, а не своя у каждой таблицы. Иначе они
 * начнут расходиться между экранами: здесь «данные свежие», рядом
 * «данные за вчера» — и верить перестанут обеим.
 *
 * ПОРОГИ СЧИТАЮТСЯ ОТ ПЕРИОДА КАЖДОГО ИСТОЧНИКА — см. `health.ts`.
 * Общие пороги из ТЗ 13.3 остаются нижней границей, но применять их
 * к источнику, который сам обновляется раз в шесть часов, значит
 * зажечь плашку навсегда.
 */

export function HealthBand({ wsId, cabinetId }: { wsId: string; cabinetId: string }) {
  const { data, isLoading, error } = useDataHealth(wsId, cabinetId);

  if (isLoading) return null;

  if (error || !data) {
    // Молчать нельзя: невозможность узнать достоверность — сама по себе
    // сведение о достоверности, и притом плохое.
    return (
      <Band level="bad" повод="здоровье-неизвестно">
        Не удалось узнать, насколько свежи данные. Числам ниже не верьте.
      </Band>
    );
  }

  const verdict = worst(data.sources);
  if (verdict.level === 'ok') return null;

  return (
    <Band level={verdict.level} повод={verdict.text}>
      {verdict.text}
      {verdict.at && <span className="ml-2 rk-quiet">Данные на {verdict.at} МСК</span>}
    </Band>
  );
}

interface Verdict {
  level: Level;
  text: string;
  at: string | null;
}

/**
 * Худшее из всех источников.
 *
 * Именно худшее, а не среднее и не «главный источник»: кабинет показывает
 * числа из всех сразу, и достоверность целого равна достоверности слабого
 * звена. Усреднение здесь означало бы зелёную плашку над таблицей, половина
 * которой не выгрузилась.
 */
function worst(sources: DataSource[]): Verdict {
  let level: Level = 'ok';
  let text = '';
  let at: string | null = null;

  const take = (next: Level, message: string, ageMinutes: number | null): void => {
    if (rank(next) <= rank(level)) return;
    level = next;
    text = message;
    at = ageMinutes === null ? null : moscowTime(ageMinutes);
  };

  for (const s of sources) {
    const name = title(s.kind);
    const level = levelOf(s);
    if (level === 'ok') continue;

    if (s.status === 'never') {
      /**
       * СЛОВА ИЗ ЭТАЛОНА, И ОНИ ТОЧНЕЕ ПРЕЖНИХ. Стояло «ни разу
       * не синхронизировалось. Числа показывать не из чего» — а это
       * неправда всякий раз, когда не выгрузился ОДИН источник
       * из четырёх: кампании на экране есть, расход есть, нет отзывов.
       * Полоса, преувеличивающая беду, стоит ровно столько же, сколько
       * полоса, её преуменьшающая.
       */
      take('bad', `${name}: ни разу не ${синхроном(s.kind)} — часть данных на экране неполна`, null);
      continue;
    }
    if (!s.complete && s.status !== 'running') {
      /**
       * ═══════════════════════════════════════════════════════════════
       * ГОВОРИМ ТО, ЧТО ПРАВДА ЗАПРЕЩЕНО, А НЕ ТО, ЧТО СТРАШНЕЕ ЗВУЧИТ.
       *
       * Здесь стояло «Правила приостановлены». 15.09.2026 проверка
       * показала, что это неправда: ни сторож ставок (`logic:guards`,
       * раз в 15 минут), ни правила исключения на свежесть выгрузки
       * не смотрят — они работают и продолжают ставить кампании
       * на паузу. Единственное, что закрыто по неполным данным, —
       * ЗАПУСК НОВОГО теста ставок (`data_stale` на создании).
       *
       * Полоса кабинета существует ровно затем, чтобы ей верили.
       * Строка, пугающая тем, чего не происходит, тратит это доверие
       * и прикрывает то, что происходит на самом деле.
       *
       * Текст причины берём у API: он написан там, где она известна,
       * и в нём есть числа — «не получено 12 из 400».
       * ═══════════════════════════════════════════════════════════════
       */
      take(
        'bad',
        `${name}: ${s.note ?? 'выгрузка неполная'}. Новые тесты ставок пока не запустить.`,
        s.ageMinutes,
      );
      continue;
    }

    // ВОЗРАСТ НАЗЫВАЕМ ВМЕСТЕ С ТЕМ, ЧЕГО ЖДАЛИ. «Данные старше 9 ч»
    // без периода читается как поломка у любого источника; «при обычных
    // 6 ч» сразу говорит, насколько всё запущено.
    const t = thresholds(s.everyMinutes);
    const usual =
      typeof s.everyMinutes === 'number' && s.everyMinutes > 0
        ? ` — выгрузка идёт раз в ${срок(s.everyMinutes)}, значит её пропустили`
        : '';
    take(level, `${name}: данные старше ${срок(s.ageMinutes ?? t.yellow)}${usual}.`, s.ageMinutes);
  }

  return { level, text, at };
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СРОК СЛОВАМИ — И МИНУТЫ НАЗВАНЫ МИНУТАМИ.
 *
 * Здесь стояло одно деление на шестьдесят с округлением вниз, и выгрузка
 * расхода — она ходит раз в полчаса — объявлялась идущей «раз в 0 ч».
 * Снимок владельца 13.09.2026: «Статистика: данные старше 13 ч —
 * выгрузка идёт раз в 0 ч, значит её пропустили».
 *
 * Возраст на том снимке был назван верно, и пропуск был настоящий —
 * обход частотности запирал кабинет по пятьдесят минут в час. Но строка,
 * противоречащая сама себе, отнимает доверие и у числа рядом: прочитав
 * «раз в 0 ч», человек перестаёт верить и «13 ч». Та же беда, что стоила
 * доверия плитке 10.09 («1 кампания тратят»).
 *
 * ИМЯ СМЕНИЛОСЬ ВМЕСТЕ С ПОВЕДЕНИЕМ. `hours`, возвращающая «30 мин», —
 * это ловушка для следующего читателя: он возьмёт её ради часов
 * и получит минуты.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function срок(minutes: number): string {
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))} мин`;
  const h = Math.floor(minutes / 60);
  if (h < 24) return `${h} ч`;
  const d = Math.floor(h / 24);
  return `${d} сут`;
}

/**
 * Время последней выгрузки по московскому поясу.
 *
 * Пояс задан жёстко и намеренно: сутки в отчётах Wildberries московские,
 * и «данные на 23:40» в часовом поясе продавца из Владивостока означало бы
 * другой день, чем тот, за который посчитаны числа рядом.
 */
export function moscowTime(ageMinutes: number): string {
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ВСПОМОГАТЕЛЬНОЕ НЕ РОНЯЕТ ОСНОВНОЕ.
   *
   * `Intl.DateTimeFormat.format` на `Invalid Date` не возвращает
   * странную строку, а БРОСАЕТ. Полоса свежести живёт в каркасе
   * над всеми экранами: одно нечисло в ответе — и вместо кабинета
   * белый экран.
   *
   * Поймано снимком экрана: заглушка прислала поле под другим именем,
   * `ageMinutes` пришёл `undefined`, и каркас упал целиком.
   * ═══════════════════════════════════════════════════════════════════
   */
  if (!Number.isFinite(ageMinutes)) return '—';

  const at = new Date(Date.now() - ageMinutes * 60_000);
  return ЧАСЫ.format(at);
}

/**
 * То же время, но С ДАТОЙ И ПРЕДЛОГОМ: «15.08.2026 в 09:14».
 *
 * ПОЧЕМУ НЕ ОДНО НА ДВОИХ. В полосе свежести время стоит В СТРОКЕ
 * ИСТОЧНИКОВ — «Кампании 09:14 · Статистика 09:14 · Кластеры вчера», —
 * и дата рядом с каждым из четырёх повторилась бы четырежды. А в подписи
 * под заголовком оно одно, и без даты не отвечает на свой вопрос:
 * «09:14» одинаково читается и как сегодняшнее утро, и как позавчерашнее.
 *
 * ТАК В ЭТАЛОНЕ, дословно: «Выгружено 15.08.2026 в 09:14 · 36 кампаний».
 * Предлог «в» — часть подписи, а не разделитель: без него выходит
 * «Выгружено 15.08.2026 09:14», где две величины стоят подряд
 * и читаются как одно длинное число.
 */
export function moscowDateTime(ageMinutes: number): string {
  if (!Number.isFinite(ageMinutes)) return '—';

  const at = new Date(Date.now() - ageMinutes * 60_000);
  const дата = ДАТА.format(at);
  return `${дата} в ${moscowTime(ageMinutes)}`;
}

/**
 * «синхронизировались» или «синхронизировалась» — по роду названия.
 *
 * ОДНО СЛОВО НА ВСЕХ НЕ ВЫХОДИТ. «Статистика» женского рода
 * и единственного числа, остальные три названия — множественного:
 * «Отзывы ни разу не синхронизировалась» читается как поломка кода,
 * а не как сообщение о поломке выгрузки. Средний род («ни разу
 * не синхронизировалось»), который стоял тут раньше, не подходит
 * ни одному из четырёх.
 */
function синхроном(kind: string): string {
  return kind === 'stats' ? 'синхронизировалась' : 'синхронизировались';
}

function title(kind: string): string {
  switch (kind) {
    case 'campaigns':
      return 'Кампании';
    case 'stats':
      return 'Статистика';
    case 'clusters':
      return 'Кластеры';
    case 'reviews':
      return 'Отзывы';
    default:
      return kind;
  }
}

/**
 * Полоса тревоги.
 *
 * ФОН БЕРЁТСЯ ГОТОВЫМ ШАГОМ, А НЕ СМЕШИВАНИЕМ С БЕЛЫМ. Здесь стояло
 * `color-mix(… , white)`, и на светлой теме это выглядело верно ровно
 * до появления тёмной: смешанный с белым красный остаётся светло-розовым
 * на чёрном экране, а текст поверх него — тёмно-красным по светло-розовому
 * в окружении тёмного. Полоса, которая кричит громче всего на экране
 * и при этом не читается, — худший из возможных исходов для полосы
 * тревоги.
 *
 * Готовые шаги (`--bad-wash` и `--bad-ink`) заданы для каждой темы
 * отдельно и измерены на своей подложке.
 */
/**
 * Полоса над всем кабинетом.
 *
 * ВЫНЕСЕНА НАРУЖУ, ЧТОБЫ ВТОРАЯ ПОЛОСА НЕ БЫЛА ВТОРОЙ НА ВИД. Про срок
 * подписки говорит `billing/plan-band.tsx`, и если он нарисует свою
 * полосу «примерно такую же», две тревоги на одном экране окажутся
 * разного роста, цвета и с разными значками — то есть будут выглядеть
 * как поломка вёрстки, а не как два сообщения.
 */
export function Band({
  level,
  повод,
  children,
}: {
  level: Exclude<Level, 'ok'>;
  /**
   * ПОВОД — ЭТО ТО, ПРО ЧТО ПОЛОСА. Закрытие держится за него, а не
   * за саму полосу: человек закрыл «Отзывы: выгрузка неполная», через
   * час сломались кампании — и полоса ОБЯЗАНА появиться снова.
   * Закрытие без повода означало бы «больше не говори мне ничего»,
   * а нажимали на «эту я прочитал».
   */
  повод: string;
  children: React.ReactNode;
}) {
  /* Полоса образца — красная. Оранжевая нужна для «данные несвежие»:
     это тревога, но не отказ, и красным её звать значит однажды перестать
     замечать красный вовсе. Цвет подменяется на месте, всё остальное —
     из общей части. */
  const warn = level !== 'bad';
  const [закрыт, setЗакрыт] = useState<string | null>(null);
  if (закрыт === повод) return null;

  return (
    <div
      role="status"
      data-d="полоса-предупреждения"
      className={K.полосаПредупреждения}
      style={
        warn
          ? {
              background: 'var(--warn-wash)',
              color: 'var(--warn-ink)',
              borderBottomColor: 'color-mix(in oklab, var(--warn) 30%, transparent)',
            }
          : undefined
      }
    >
      <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" style={{ flex: '0 0 auto' }}>
        <path d="M8 1.5 15 14H1zM7.2 6h1.6v4H7.2zm0 5h1.6v1.6H7.2z" />
      </svg>
      {/* ТЕКСТ В СВОЕЙ ОБЁРТКЕ (`flex: 1 1 0`) — так в эталоне. Без неё
          крестик встаёт вплотную к последнему слову вместо правого края,
          и полоса читается как строка с опечаткой. */}
      <span className={K.текстПредупреждения}>{children}</span>
      <button
        type="button"
        aria-label="Закрыть"
        className={K.кнопкаЗакрытьПредупреждение}
        onClick={() => setЗакрыт(повод)}
      >
        <IconClose size={14} />
      </button>
    </div>
  );
}
